import {
  BookOpenIcon,
  ChatAlt2Icon,
  ChatIcon,
  ClipboardListIcon,
  CodeIcon,
  CubeIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  KeyIcon,
  MailIcon,
  ReceiptRefundIcon,
  SupportIcon,
  TruckIcon,
  UsersIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import { useLanguagesFaq } from '../sharedFaqs';
import type { IndustryContent } from '../types';
import EscalationEmail from '../visuals/EscalationEmail';
import { tone } from '../../primitives';

const BRAND_BG = 'bg-[#2f5d50]';

export function useCustomerServiceContent(): IndustryContent {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');
  const languagesFaq = useLanguagesFaq();

  return {
    key: 'customer-service',
    seoTitle: t('Customer Service Chatbot: AI Agent for 24/7 Support'),
    seoDescription: t(
      'Build an AI customer service agent trained on your help center and policies. It answers on your website, email, WhatsApp and the phone, day and night, and emails your team when a case needs a person.',
    ),
    hero: {
      label: t('AI customer service chatbot'),
      title: t('Customer service that answers in seconds.'),
      accent: t('Day and night, on every channel.'),
      lead: t(
        'Train an agent on your help center, policies and past answers. It resolves everyday questions on your website, email, WhatsApp and the phone, and passes anything unusual to your team with the details already collected.',
      ),
      description: t(
        'Example: a customer asks an online store where their order is. The agent looks it up, shares the delivery date, and passes a request to change the address on to the support team.',
      ),
      surface: 'widget',
      brand: {
        name: 'Halden',
        initial: 'H',
        kind: t('Home goods store'),
        bg: BRAND_BG,
      },
      messages: [
        { from: 'visitor', text: tc('Where is my order #4821?'), at: 600 },
        {
          from: 'agent',
          typingAt: 1100,
          at: 1900,
          content: <OrderStatus />,
        },
        {
          from: 'agent',
          at: 2500,
          content: tc(
            'It left our warehouse yesterday and arrives on Thursday. Here is your tracking link.',
          ),
        },
        {
          from: 'visitor',
          text: t('Can I still change the delivery address?'),
          at: 3500,
        },
        {
          from: 'agent',
          typingAt: 3900,
          at: 4800,
          content: t(
            'It’s already with the courier, so I can’t change it here. I’ve passed your request to our team, and they’ll email you within the hour.',
          ),
        },
      ],
      steps: [
        {
          icon: CodeIcon,
          title: t('Looked up order #4821'),
          via: tc('Custom action'),
          at: 1400,
        },
        {
          icon: BookOpenIcon,
          title: t('Checked the delivery policy'),
          via: tc('Knowledge'),
          at: 4100,
        },
        {
          icon: MailIcon,
          title: t('Emailed your support team'),
          via: tc('Email'),
          at: 4600,
        },
      ],
    },
    jobs: {
      title: t('The questions that fill your queue.'),
      tail: t('Answered before anyone waits.'),
      lead: t(
        'Most support requests ask the same few things. Your agent handles them from your own content, so your team can focus on the conversations that need a person.',
      ),
      items: [
        {
          icon: TruckIcon,
          title: t('Order status'),
          detail: t(
            'Finds an order’s status through Shopify or your own system, once the customer confirms who they are.',
          ),
        },
        {
          icon: ReceiptRefundIcon,
          title: t('Returns and refunds'),
          detail: t(
            'Explains your return window and refund steps, and links to the right form.',
          ),
        },
        {
          icon: KeyIcon,
          title: t('Account help'),
          detail: t(
            'Walks customers through passwords, billing and settings, step by step.',
          ),
        },
        {
          icon: CubeIcon,
          title: t('Product questions'),
          detail: t(
            'Answers questions about sizes, features and compatibility from your catalog and docs.',
          ),
        },
        {
          icon: SupportIcon,
          title: t('Troubleshooting'),
          detail: t(
            'Guides customers through fixes from your help articles and how-to videos.',
          ),
        },
        {
          icon: UsersIcon,
          title: t('Handover to your team'),
          detail: t(
            'Takes the customer’s details and emails your team when a case needs a person.',
          ),
        },
      ],
    },
    workflow: {
      title: t('Built from what you already have.'),
      tail: t('No new help desk to learn.'),
      learns: [
        { icon: GlobeAltIcon, label: t('Help center') },
        { icon: DocumentTextIcon, label: t('Policies and guides') },
        { icon: VideoCameraIcon, label: t('How-to videos') },
        { icon: ChatAlt2Icon, label: t('Answers you’ve corrected') },
      ],
      acts: [
        { icon: CodeIcon, label: t('Order lookup') },
        { icon: 'FaShopify', label: 'Shopify' },
        { icon: ClipboardListIcon, label: tc('Forms') },
        { icon: MailIcon, label: t('Email your team') },
      ],
      channels: [
        { icon: ChatIcon, label: tc('Website chat') },
        { icon: 'FaWhatsapp', label: 'WhatsApp' },
        { icon: 'FaFacebookMessenger', label: 'Messenger' },
        { icon: 'Gmail', label: t('Gmail and Outlook') },
        { icon: 'FaPhone', label: tc('Phone calls') },
      ],
    },
    spotlight: {
      title: t('When it needs a person,'),
      tail: t('your team gets the whole story.'),
      lead: t(
        'Tell your agent when to step aside. It collects the customer’s details, sums up the problem and emails it to your team, so nobody has to ask the same questions twice.',
      ),
      points: [
        t('Asks for a name, email and order number in the chat'),
        t('Emails a summary to your support inbox'),
        t('Keeps every website and WhatsApp chat for you to read'),
        t('Fix a weak answer once and it stays fixed'),
      ],
      description: t(
        'Example: an email to the support team, written by the agent, with the customer’s details and a summary of the request.',
      ),
      visual: <EscalationEmail brand={BRAND_BG} />,
    },
    faqs: [
      {
        question: t('Can it look up an order?'),
        answer: t(
          'Yes. Connect Shopify and it finds an order’s status once the customer gives the order number and the email used at checkout. For other stores, a custom action can look orders up in your own system.',
        ),
      },
      {
        question: t('What happens when a customer needs a person?'),
        answer: t(
          'You decide in the instructions. The agent can take the customer’s details and email your team, or point them to your support address. The chat itself stays with the agent, and your team replies by email.',
        ),
      },
      {
        question: t('Can it answer support emails too?'),
        answer: t(
          'Yes. Forward your support address to your agent’s own email address, or connect Gmail or Outlook so it writes replies in the same thread, as drafts for you to check or sent straight away.',
        ),
      },
      {
        question: t('How do I keep its answers accurate?'),
        answer: t(
          'Train it on your help center and policies, and retrain with one click when they change. In Conversations you can read the chats and correct an answer, and the agent uses your correction from then on.',
        ),
      },
      languagesFaq,
    ],
    finalExchange: {
      question: t('Can I return something I’ve already opened?'),
      answer: t('Yes, within 30 days. Here’s the returns form.'),
    },
  };
}

/** The order the agent found, as a card in the chat. */
function OrderStatus() {
  const { t } = useLocale('bot-use-cases');
  const stages = [t('Ordered'), t('Shipped'), t('Delivered')];

  return (
    <div className='w-[85%] rounded-2xl bg-[#f6f6f9] p-3.5 ring-1 ring-black/[0.05] dark:bg-white/[0.05] dark:ring-white/[0.06]'>
      <p className='flex items-center justify-between gap-2 text-[13px]'>
        <span className={clsxm('font-semibold', tone.ink)}>
          {t('Order')} #4821
        </span>
        <span className='rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
          {t('Shipped')}
        </span>
      </p>
      <div className='mt-3 grid grid-cols-3 gap-1'>
        {stages.map((stage, index) => (
          <span
            key={stage}
            className={clsxm(
              'h-1.5 rounded-full',
              index < 2 ? BRAND_BG : 'bg-black/10 dark:bg-white/15',
            )}
          />
        ))}
      </div>
      <p className='mt-1.5 grid grid-cols-3 text-[11px]'>
        {stages.map((stage, index) => (
          <span
            key={stage}
            className={clsxm(
              'truncate',
              index === 1 ? 'text-center' : index === 2 ? 'text-end' : '',
              tone.quiet,
            )}
          >
            {stage}
          </span>
        ))}
      </p>
    </div>
  );
}
